/**
 * EligibilityPreviewBanner — preview d'éligibilité pour une œuvre.
 *
 * Affiché dans `<DraftsTab>` avant le bouton "Générer drafts" :
 *   - 0 éligible : alerte + lien vers /settings, bouton parent disabled.
 *   - Partiel : "Éligible pour X/N plateformes. Exclues : ..." (raisons FR
 *     pré-écrites par paire (platform, code) côté BE).
 *   - Tout éligible : aucun banner (réduit le bruit).
 *
 * Hook : `useWorkEligibility(workId)` — staleTime 30s, déclenche le polling
 * doux pendant le tri /drafts.
 */

import { Link } from 'react-router'

import { useWorkEligibility } from '@/hooks/useEligibility'
import { PLATFORM_FR, PLATFORM_OPTIONS } from '@/types/drafts'

interface Props {
  workId: number
}

export default function EligibilityPreviewBanner({ workId }: Props) {
  const query = useWorkEligibility(workId)

  if (query.isLoading || query.isError || !query.data) return null

  const { eligible_platforms, excluded_platforms } = query.data
  const eligibleCount = eligible_platforms.length
  const totalCount = PLATFORM_OPTIONS.length

  // Tout éligible → aucun banner (cas heureux, pas de bruit).
  if (eligibleCount === totalCount) return null

  // Aucun éligible → alerte bloquante.
  if (eligibleCount === 0) {
    return (
      <div className="eligibility-banner eligibility-banner--blocking" role="alert">
        <p className="eligibility-banner-title">Aucune plateforme éligible</p>
        <p className="eligibility-banner-message">
          Cette œuvre ne peut générer aucun draft selon les règles actuelles. Ajoute une variante
          (Variantes) ou ajuste les{' '}
          <Link to="/settings?tab=platform-rules">règles d'éligibilité</Link>.
        </p>
      </div>
    )
  }

  // Partiel — afficher exclusions
  return (
    <div className="eligibility-banner eligibility-banner--partial" role="note">
      <p className="eligibility-banner-message">
        Cette œuvre est éligible pour{' '}
        <strong>
          {eligibleCount}/{totalCount}
        </strong>{' '}
        plateformes.
      </p>
      {excluded_platforms.length > 0 && (
        <ul className="eligibility-banner-exclusions">
          {excluded_platforms.map((exc) => (
            <li key={exc.platform}>
              <strong>{PLATFORM_FR[exc.platform]}</strong> — {exc.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
